import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Modal,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/CloseRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineRounded';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAddRounded';

import { createMovementsBatch } from '../../api/movements';
import { BotMap, IManualMovement } from '../../shared/types';
import { useStagedMovements } from '../../utils/useStagedMovements';
import { ReconnectingWebSocket } from '../../socket';
import { tokens } from '../../theme';

interface CapitalMovementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message?: string) => void;
  botMap: BotMap;
  botTokens: Record<string, string[]>;
  socket: ReconnectingWebSocket;
}

interface SubmissionStatus {
  total: number;
  processed: number;
  success: number;
  errors: number;
}

type CapitalMovementType = 'DEPOSIT' | 'WITHDRAWAL';

const modalStyle = {
  position: 'absolute' as const,
  // Phones: bottom sheet; larger screens: centered card.
  top: { xs: 'auto', sm: '50%' },
  bottom: { xs: 0, sm: 'auto' },
  left: { xs: 0, sm: '50%' },
  transform: { xs: 'none', sm: 'translate(-50%, -50%)' },
  width: { xs: '100%', sm: 800 },
  maxWidth: { sm: 'calc(100vw - 32px)' },
  // Glass chrome shared with the themed dialogs.
  bgcolor: 'rgba(18,18,20,0.92)',
  backdropFilter: 'blur(24px) saturate(150%)',
  WebkitBackdropFilter: 'blur(24px) saturate(150%)',
  borderRadius: { xs: '28px 28px 0 0', sm: '28px' },
  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.1), 0 40px 90px -20px ${alpha('#000000', 0.9)}`,
  border: `1px solid ${alpha('#FFFFFF', 0.14)}`,
  borderBottom: { xs: 0, sm: `1px solid ${alpha('#FFFFFF', 0.14)}` },
  display: 'flex',
  flexDirection: 'column',
  maxHeight: { xs: 'calc(100dvh - env(safe-area-inset-top) - 12px)', sm: '90vh' },
  pb: { xs: 'env(safe-area-inset-bottom)', sm: 0 },
  overflow: 'hidden',
};

const MOVEMENT_LABEL: Record<string, string> = { DEPOSIT: 'Deposit', WITHDRAWAL: 'Withdrawal' };

/** "1 movement", "2 movements", "0 movements". */
const pluralize = (count: number, noun: string) => `${count} ${count === 1 ? noun : `${noun}s`}`;

/** Muted placeholder for empty selects (labels sit above the field, so an empty select needs one). */
const placeholder = (text: string) => (
  <Box component="span" sx={{ color: 'text.disabled' }}>
    {text}
  </Box>
);

const CapitalMovementModal: React.FC<CapitalMovementModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  botMap,
  botTokens,
  socket,
}) => {
  const theme = useTheme();
  const { stagedMovements, addMovement, removeMovement, clearMovements } = useStagedMovements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [feedbackSeverity, setFeedbackSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus | null>(null);

  // Form state
  const [movementType, setMovementType] = useState<CapitalMovementType>('DEPOSIT');
  const [selectedBotId, setSelectedBotId] = useState<string>('');
  const [token, setToken] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');

  const availableTokens = useMemo(() => botTokens[selectedBotId] || [], [selectedBotId, botTokens]);
  
  const currentAmountUSD = useMemo(() => {
    const q = parseFloat(quantity);
    const p = parseFloat(price);
    return !isNaN(q) && !isNaN(p) ? q * p : 0;
  }, [quantity, price]);

  const resetForm = () => {
    setSelectedBotId('');
    setToken('');
    setQuantity('');
    setPrice('');
  };

  const date = useMemo(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const progressLabel = submissionStatus
    ? `Processing ${submissionStatus.processed} of ${pluralize(submissionStatus.total, 'movement')}…`
    : `Submitting ${pluralize(stagedMovements.length, 'movement')}…`;

  const handleRequestClose = (_event?: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (isSubmitting) return;
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      clearMovements();
      resetForm();
      setIsSubmitting(false);
      setSubmissionStatus(null);
      setFeedbackMessage(null);
      setFeedbackSeverity('info');
      setMovementType('DEPOSIT');
    }
  }, [isOpen, clearMovements]);

  useEffect(() => {
    const handleProgress = (payload: SubmissionStatus & { message?: string }) => {
      setSubmissionStatus(payload);
      setFeedbackSeverity('info');
      setFeedbackMessage(payload.message || `Processing ${payload.processed} of ${pluralize(payload.total, 'movement')}…`);
    };

    const handleCompleted = (payload: { message?: string; total: number; success: number }) => {
      const message = payload?.message || `Processed ${payload.success} of ${pluralize(payload.total, 'movement')} successfully`;
      setFeedbackSeverity('success');
      setFeedbackMessage(message);
      setIsSubmitting(false);
      setSubmissionStatus(null);
      clearMovements();
      resetForm();
      setMovementType('DEPOSIT');
      onSuccess?.(message);
      onClose();
    };

    const handleError = (payload: any) => {
      const message = payload?.error || 'Failed to process movements batch.';
      setFeedbackSeverity('error');
      setFeedbackMessage(message);
      setIsSubmitting(false);
      setSubmissionStatus(null);
    };

    socket.on('movementBatchProgress', handleProgress);
    socket.on('movementBatchCompleted', handleCompleted);
    socket.on('movementBatchError', handleError);

    return () => {
      socket.off('movementBatchProgress', handleProgress);
      socket.off('movementBatchCompleted', handleCompleted);
      socket.off('movementBatchError', handleError);
    };
  }, [socket, clearMovements, onClose, onSuccess]);

  const handleAddToStage = () => {
    if (isSubmitting) return;
    if (!selectedBotId || !token || !quantity || !price) return;

    const newMovement: IManualMovement = {
      id: `${selectedBotId}-${Date.now()}-${Math.random()}`,
      botId: selectedBotId,
      movementType,
      date,
      token,
      quantity: parseFloat(quantity),
      price: parseFloat(price),
      amountUSD: currentAmountUSD,
    };

    addMovement(newMovement);
    resetForm();
  };

  /**
   * Sends the staged batch. Only the Execute button calls it: the modal is not a form, so Enter in a
   * field can't send a batch the user is still adding to.
   */
  const handleExecute = () => {
    if (isSubmitting || stagedMovements.length === 0) return;

    setIsSubmitting(true);
    setFeedbackSeverity('info');
    setFeedbackMessage(`Submitting ${pluralize(stagedMovements.length, 'movement')}…`);
    setSubmissionStatus({
      total: stagedMovements.length,
      processed: 0,
      success: 0,
      errors: 0,
    });

    createMovementsBatch(socket, stagedMovements);
  };

  const canAddToStage = Boolean(selectedBotId && token && quantity && price);

  return (
    <Modal open={isOpen} onClose={handleRequestClose} closeAfterTransition>
      <>
        <Box sx={modalStyle}>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" component="h2" fontWeight={700}>
                Capital movement
              </Typography>
              <IconButton onClick={handleRequestClose} disabled={isSubmitting} aria-label="Close" sx={{ width: 44, height: 44, mr: -1 }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Tabs
              value={movementType}
              onChange={(_, value) => value && setMovementType(value as CapitalMovementType)}
              variant="fullWidth"
              TabIndicatorProps={{
                sx: {
                  top: 0,
                  height: '100%',
                  bgcolor: 'rgba(255,255,255,0.13)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)',
                  zIndex: 0,
                  borderRadius: 999
                }
              }}
              sx={{
                p: '4px',
                minHeight: 0,
                bgcolor: alpha(theme.palette.common.black, 0.35),
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 999,
                '& .MuiTab-root': {
                  zIndex: 1,
                  minHeight: 40,
                  borderRadius: 999,
                  color: 'text.secondary',
                  fontWeight: 650,
                  '&.Mui-selected': { color: 'text.primary' }
                }
              }}
            >
              <Tab icon={<AddCircleOutlineIcon />} iconPosition="start" label="Deposit" value="DEPOSIT" />
              <Tab icon={<RemoveCircleOutlineIcon />} iconPosition="start" label="Withdrawal" value="WITHDRAWAL" />
            </Tabs>
          </Box>

          {/* Content: sized to what it holds (capped at 90vh, then scrolls). Side by side, both columns
              share the row's height, so the review queue ends where the form ends. */}
          <Grid
            container
            columnSpacing={4}
            rowSpacing={3}
            sx={{ p: 2, flex: '0 1 auto', minHeight: 0, overflowY: 'auto', alignItems: 'stretch' }}
          >
            {/* Left Panel - Form (natural height: a long queue doesn't push "Stage movement" down) */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="overline" color="text.secondary">Step 1 · Define movement</Typography>
              <Stack spacing={2} mt={2}>
                <TextField
                  select
                  label="Target bot"
                  value={selectedBotId}
                  onChange={e => setSelectedBotId(e.target.value)}
                  fullWidth
                  disabled={isSubmitting}
                  slotProps={{
                    select: {
                      displayEmpty: true,
                      renderValue: (value) =>
                        value ? botMap[value as string]?.name ?? (value as string) : placeholder('Select bot'),
                    },
                  }}
                >
                  {Object.entries(botMap)
                    .sort(([, botA], [, botB]) => botA.name.localeCompare(botB.name))
                    .map(([id, details]) => (
                      <MenuItem key={id} value={id}>
                        {details.name}
                      </MenuItem>
                    ))}
                </TextField>
                <Stack direction="row" spacing={2}> 
                  <TextField
                    select
                    label="Token"
                    value={token}
                    onChange={e => setToken(e.target.value)}
                    fullWidth
                    disabled={!selectedBotId || isSubmitting}
                    slotProps={{
                      select: {
                        displayEmpty: true,
                        renderValue: (value) => (value ? (value as string) : placeholder('Select token')),
                      },
                    }}
                    // Disabled until a bot is chosen: keep the theme hairline instead of MUI's
                    // brighter disabled outline (palette.action.disabled, 30%).
                    sx={{ '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: tokens.border } }}
                  >
                    {(availableTokens as string[]).map(t => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Quantity"
                    type="number"
                    placeholder="0.00"
                    inputProps={{ inputMode: "decimal" }}
                    value={quantity}
                    onChange={e => setQuantity(e.target.value)}
                    fullWidth
                    disabled={isSubmitting}
                  />
                </Stack>
                <TextField
                  label="Price"
                  type="number"
                  inputProps={{ inputMode: "decimal" }}
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  fullWidth
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  disabled={isSubmitting}
                />
                {currentAmountUSD > 0 && (
                  <Alert
                    severity="info"
                    // A note, not a state: neutral grey (blue is the Dipster's identity).
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.10)',
                      color: 'text.primary',
                      '& .MuiAlert-icon': { color: 'text.secondary' },
                    }}
                  >
                    Total Value: <strong>${currentAmountUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </Alert>
                )}
              </Stack>
              <Button
                variant="outlined"
                color="primary"
                disabled={!canAddToStage || isSubmitting}
                onClick={handleAddToStage}
                startIcon={<PlaylistAddIcon />}
                sx={{ mt: 2 }}
              >
                Stage movement
              </Button>
            </Grid>

            {/* Right Panel - Staged Movements */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
              <Typography variant="overline" color="text.secondary">
                Step 2 · Review queue ({stagedMovements.length})
              </Typography>
              {/* Fills the column (at least 200px), so it ends on the same line as the form; the empty
                  state is centred in it and staged movements list from the top. */}
              <Box sx={{
                mt: 1,
                flex: '1 1 auto',
                alignSelf: 'stretch',
                minHeight: 200,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: alpha(theme.palette.common.black, 0.2),
                borderRadius: 2,
                p: 1,
              }}>
                {stagedMovements.length === 0 ? (
                  <Stack justifyContent="center" alignItems="center" sx={{ flex: 1, color: 'text.disabled', p: 2, textAlign: 'center' }}>
                    <Typography>No movements staged.</Typography>
                    <Typography variant="caption">Stage a movement to add it here.</Typography>
                  </Stack>
                ) : (
                  <List dense>
                    {stagedMovements.map(m => (
                      <ListItem
                        key={m.id}
                        secondaryAction={
                          <IconButton edge="end" size="small" onClick={() => removeMovement(m.id)} disabled={isSubmitting}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                        sx={{
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          mb: 1,
                          '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.5) }
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontWeight: 500, noWrap: true }}
                          secondaryTypographyProps={{ fontFamily: 'monospace' }}
                          primary={`${botMap[m.botId]?.name || m.botId} · ${MOVEMENT_LABEL[m.movementType] ?? m.movementType}`}
                          secondary={`${m.quantity} ${m.token} @ $${Number(m.price).toLocaleString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Footer */}
          <Box sx={{ mt: 'auto', flexShrink: 0, p: 2, borderTop: 1, borderColor: 'divider' }}>
            {/* Phones: full-width button, helper below. Larger screens: helper beside the button. */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="flex-end"
              spacing={{ xs: 1, sm: 2 }}
            >
              {stagedMovements.length === 0 && !isSubmitting && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ order: { xs: 1, sm: 0 }, textAlign: { xs: 'center', sm: 'right' } }}
                >
                  Stage at least one movement
                </Typography>
              )}
              <Button
                type="button"
                variant="contained"
                color="secondary"
                size="large"
                disabled={isSubmitting || stagedMovements.length === 0}
                onClick={handleExecute}
                sx={{ minWidth: { sm: 260 } }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : stagedMovements.length === 0 ? (
                  'Execute'
                ) : (
                  `Execute ${pluralize(stagedMovements.length, 'movement')}`
                )}
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* Loading Overlay */}
        <Backdrop open={isSubmitting} sx={{ color: '#fff', zIndex: theme.zIndex.modal + 1 }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="body2">{progressLabel}</Typography>
          </Stack>
        </Backdrop>

        {/* Feedback Snackbar */}
        {feedbackMessage && (
          <Snackbar
            open={!!feedbackMessage}
            autoHideDuration={feedbackSeverity === 'success' ? 3000 : 6000}
            onClose={() => setFeedbackMessage(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setFeedbackMessage(null)} severity={feedbackSeverity} sx={{ width: '100%' }}>
              {feedbackMessage}
            </Alert>
          </Snackbar>
        )}
      </>
    </Modal>
  );
};

export default CapitalMovementModal;
